import { Injectable } from '@angular/core';
import { Cell, Graph, Node } from '@antv/x6';
import { he } from 'date-fns/locale';

/* We have added invisible hover shapes around the nodes, to see them enable this flag */
export const SHOW_INVISIBLE_HOVER_SHAPES = false;

@Injectable({
  providedIn: 'root'
})
export class GraphService {

  constructor() { }

  public successColor = 'rgb(29, 209, 9)';
  public failureColor = 'rgb(255, 0, 0)';
  public defaultColor = 'rgb(126, 129, 134)';

  public stepFunctionBody = {
    rx: 6,
    ry: 6,
    stroke: 'var(--workflow-step-function-border-color)',
    fill: 'var(--workflow-step-function-background-color)',
    strokeWidth: 4,
    refWidth: '100%',
    refHeight: '100%'
  };

  public nodeLabel = {
    fill: 'var(--text-color)',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'inherit'
  };

  public aiIcon = {
    'xlink:href': 'data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="' + this.defaultColor + '"><path d="M320 0c13.3 0 24 10.7 24 24l0 72 104 0c53 0 96 43 96 96l0 224c0 53-43 96-96 96l-256 0c-53 0-96-43-96-96l0-224c0-53 43-96 96-96l104 0 0-72c0-13.3 10.7-24 24-24zM192 144c-26.5 0-48 21.5-48 48l0 224c0 26.5 21.5 48 48 48l256 0c26.5 0 48-21.5 48-48l0-224c0-26.5-21.5-48-48-48l-128 0-128 0zM48 224l16 0 0 192-16 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm544 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-16 0 0-192 16 0zM208 384l32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm96 0l32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm96 0l32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zM200 256a40 40 0 1 1 80 0 40 40 0 1 1 -80 0zm200-40a40 40 0 1 1 0 80 40 40 0 1 1 0-80z"/></svg>',
    fill: 'var(--workflow-step-function-border-color)',
    width: 50,
    height: 50,
    x: 20,
    y: 22,
    yAlignment: 'middle',
    cursor: 'pointer',
    event: 'icon:click'
  };

  public customFunctionIcon = {
    'xlink:href': 'data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="' + this.defaultColor + '"><path d="M392.8 1.2c-17-4.9-34.7 5-39.6 22l-128 448c-4.9 17 5 34.7 22 39.6s34.7-5 39.6-22l128-448c4.9-17-5-34.7-22-39.6zm80.6 120.1c-12.5 12.5-12.5 32.8 0 45.3L562.7 256l-89.4 89.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l112-112c12.5-12.5 12.5-32.8 0-45.3l-112-112c-12.5-12.5-32.8-12.5-45.3 0zm-306.7 0c-12.5-12.5-32.8-12.5-45.3 0l-112 112c-12.5 12.5-12.5 32.8 0 45.3l112 112c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256l89.4-89.4c12.5-12.5 12.5-32.8 0-45.3z"/></svg>',
    width: 50,
    height: 50,
    x: 20,
    y: 25,
    yAlignment: 'middle',
    cursor: 'pointer',
    event: 'icon:click'
  };

  public endPointStepIcon = {
    'xlink:href': 'data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="' + this.defaultColor + '"><path d="M128 24c0-13.3-10.7-24-24-24S80 10.7 80 24l0 88 48 0 0-88zm176 0c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 88 48 0 0-88zM24 144c-13.3 0-24 10.7-24 24s10.7 24 24 24l8 0 0 64c0 80.2 59 146.6 136 158.2l0 73.8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-73.8c77-11.6 136-78 136-158.2l0-64 8 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-8 0-48 0L80 144l-48 0-8 0zM192 368c-61.9 0-112-50.1-112-112l0-64 224 0 0 64c0 61.9-50.1 112-112 112z"/></svg>',
    width: 50,
    height: 50,
    x: 20,
    y: 22,
    yAlignment: 'middle',
    cursor: 'pointer',
    event: 'icon:click'
  };

  public successPlusIcon = {
    'xlink:href': 'data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="' + this.successColor + '"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0-13.3-10.7 24-24 24s-24-10.7-24-24z"/></svg>',
    width: 30,
    height: 30,
    x: 20,
    y: 22,
    yAlignment: 'middle',
    cursor: 'pointer',
    event: 'icon:click'
  };

  public failurePlusIcon = {
    'xlink:href': 'data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="' + this.failureColor + '"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0-13.3-10.7 24-24 24s-24-10.7-24-24z"/></svg>',
    width: 30,
    height: 30,
    x: 20,
    y: 22,
    yAlignment: 'middle',
    cursor: 'pointer',
    event: 'icon:click'
  };

  public plusIcon = {
    'xlink:href': 'data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="' + this.defaultColor + '"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0-13.3-10.7 24-24 24s-24-10.7-24-24z"/></svg>',
    width: 30,
    height: 30,
    x: 20,
    y: 22,
    yAlignment: 'middle',
    cursor: 'pointer',
    event: 'icon:click'
  };

  hoverShapes = [
    { tagName: 'rect', selector: 'hover-top-left' },
    { tagName: 'rect', selector: 'hover-top-right' },
    { tagName: 'rect', selector: 'hover-bottom-left' },
    { tagName: 'rect', selector: 'hover-bottom-right' },
  ];

  stepFunctionMarkup = [
    {
      tagName: 'rect',
      selector: 'body',
    },
    {
      tagName: 'text',
      selector: 'label',
    },
    {
      tagName: 'image',
      selector: 'icon'
    },
    ...this.hoverShapes
  ];

  getHoverShapeAttributes(
    shapeWidth: number, shapeHeight: number, verticalPosition: 'top' | 'bottom', horizontalPosition: 'left' | 'right'
  ) {
    const gap = 10; // gap between the hover shapes
    const margin = 25; // outside margin from the edge of the node, where the hover shape will be placed
    const config = {
      width: shapeWidth / 2 + margin,
      height: shapeHeight / 2 + margin,
      x: horizontalPosition === 'left' ? -1 * (margin + gap / 2) : (shapeWidth / 2) + (gap / 2),
      y: verticalPosition === 'top' ? -1 * (margin + gap / 2) : (shapeHeight / 2) + (gap / 2),
      fill: '#000000',
      stroke: 'none',
      fillOpacity: SHOW_INVISIBLE_HOVER_SHAPES ? 1 : 0.001,
      pointerEvents: 'visiblePainted',
      zIndex: -999, // must be very low to prevent affecting bounding box
    };
    return config;
  }

  getHoverShapeConfigs(shapeWidth: number, shapeHeight: number) {
    return {
      'hover-top-right': this.getHoverShapeAttributes(shapeWidth, shapeHeight, 'top', 'right'),
      'hover-top-left': this.getHoverShapeAttributes(shapeWidth, shapeHeight, 'top', 'left'),
      'hover-bottom-right': this.getHoverShapeAttributes(shapeWidth, shapeHeight, 'bottom', 'right'),
      'hover-bottom-left': this.getHoverShapeAttributes(shapeWidth, shapeHeight, 'bottom', 'left'),
    };
  }

  registerWorkflowEvent() {
    Graph.registerNode(
      'workflowEvent',
      {
        inherit: 'circle',
        width: 50,
        height: 50,
        markup: [
          {
            tagName: 'circle',
            selector: 'body'
          },
          {
            tagName: 'text',
            selector: 'label'
          },
          ...this.hoverShapes
        ],
        attrs: {
          body: {
            strokeWidth: 4,
            stroke: '#1890ff',
            fill: 'var(--workflow-step-function-background-color)',
          },
          label: {
            fontSize: 12,
            fill: 'var(--text-color)',
            screenTop: 25,
            refDy: -5
          },
          ...this.getHoverShapeConfigs(50, 50),
        }
      },
      true
    );

    Graph.registerNode(
      'workflowEventSuccess',
      {
        inherit: 'circle',
        width: 50,
        height: 50,
        markup: [
          {
            tagName: 'circle',
            selector: 'body'
          },
          {
            tagName: 'text',
            selector: 'label'
          },
          ...this.hoverShapes
        ],
        attrs: {
          body: {
            strokeWidth: 4,
            stroke: '#1DD109',
            fill: 'var(--workflow-step-function-background-color)',
          },
          label: {
            fontSize: 12,
            fill: 'var(--text-color)',
            screenTop: 25,
            refDy: -5
          },
          ...this.getHoverShapeConfigs(50, 50),
        }
      },
      true
    );

    Graph.registerNode(
      'workflowEventFailure',
      {
        inherit: 'circle',
        width: 50,
        height: 50,
        markup: [
          {
            tagName: 'circle',
            selector: 'body'
          },
          {
            tagName: 'text',
            selector: 'label'
          },
          ...this.hoverShapes
        ],
        attrs: {
          body: {
            strokeWidth: 4,
            stroke: '#FF0000',
            fill: 'var(--workflow-step-function-background-color)',
          },
          label: {
            fontSize: 12,
            fill: 'var(--text-color)',
            screenTop: 25,
            refDy: -5
          },
          ...this.getHoverShapeConfigs(50, 50),
        },
      },
      true
    );

    Graph.registerNode(
      'workflowEventFailurePlaceholder',
      {
        inherit: 'circle',
        attrs: {
          body: {
            strokeWidth: 4,
            stroke: '#FF0000',
            strokeDasharray: 5,
            fill: 'transparent',
            refDy: -38
          },
          label: {
            fontSize: 12,
            fill: 'var(--text-color)',
            screenTop: 25,
            refDy: 5
          },
        },
      },
      true
    );

    Graph.registerNode(
      'workflowEventSuccessPlaceholder',
      {
        inherit: 'circle',
        attrs: {
          body: {
            strokeWidth: 4,
            stroke: '#1DD109',
            strokeDasharray: 5,
            fill: 'transparent',
            refDy: -38
          },
          label: {
            fontSize: 12,
            fill: 'var(--text-color)',
            screenTop: 25,
            refDy: 5
          },
        },
      },
      true
    );

    Graph.registerNode(
      'workflowEventPlaceholder',
      {
        inherit: 'circle',
        attrs: {
          body: {
            strokeWidth: 4,
            stroke: '#1890ff',
            strokeDasharray: 5,
            fill: 'transparent',
            refDy: -38
          },
          label: {
            fontSize: 12,
            fill: 'var(--text-color)',
            screenTop: 25,
            refDy: 5
          },
        },
      },
      true
    );
  }

  /* default workflow step function - base */
  registerWorkflowStepFunction() {
    Graph.registerNode(
      'workflowStepFunction',
      {
        inherit: 'rect',
        width: 182,
        height: 100,
        markup: this.stepFunctionMarkup,
        attrs: {
          body: this.stepFunctionBody,
          label: this.nodeLabel,
          icon: this.aiIcon,
          ...this.getHoverShapeConfigs(182, 100),
        },
      },
      true
    );

    Graph.registerNode(
      'workflowStepFunction-ai-function',
      {
        inherit: 'rect',
        width: 182,
        height: 100,
        markup: this.stepFunctionMarkup,
        attrs: {
          body: this.stepFunctionBody,
          label: this.nodeLabel,
          icon: this.aiIcon,
          ...this.getHoverShapeConfigs(182, 100),
        },
      },
      true
    );

    Graph.registerNode(
      'workflowStepFunction-custom-function-definition',
      {
        inherit: 'rect',
        width: 182,
        height: 100,
        markup: this.stepFunctionMarkup,
        attrs: {
          body: this.stepFunctionBody,
          label: this.nodeLabel,
          icon: this.customFunctionIcon,
          ...this.getHoverShapeConfigs(182, 100),
        },
      },
      true
    );

    Graph.registerNode(
      'workflowStepFunction-endpoint',
      {
        inherit: 'rect',
        width: 182,
        height: 100,
        markup: this.stepFunctionMarkup,
        attrs: {
          body: this.stepFunctionBody,
          label: this.nodeLabel,
          icon: this.endPointStepIcon,
          ...this.getHoverShapeConfigs(182, 100),
        },
      },
      true
    );

    Graph.registerNode(
      'workflowStepFunctionPlaceholder',
      {
        inherit: 'rect',
        width: 182,
        height: 100,
        markup: [
          {
            tagName: 'rect',
            selector: 'body',
          },
          {
            tagName: 'text',
            selector: 'description',
          },
          {
            tagName: 'text',
            selector: 'label',
          },
        ],
        attrs: {
          body: {
            rx: 6,
            ry: 6,
            stroke: '#5F95FF',
            strokeDasharray: 5,
            fill: 'transparent',
            strokeWidth: 1,
            refWidth: '100%',
            refHeight: '100%',
          },
          description: {
            refX: '50%',
            refY: '0%',
            refDy: -60,
            textAnchor: 'middle',
            textVerticalAnchor: 'top',
            fill: 'var(--text-color)',
            fontSize: 12,
            fontFamily: 'inherit',
            text: 'Please select a function\nfrom the right side menu',
          },
          label: {
            refX: '50%',
            refY: '120%',
            refDy: -100,
            textAnchor: 'middle',
            textVerticalAnchor: 'top',
            fill: 'var(--text-color)',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'inherit'
          },
        },
      },
      true
    );
  }

  registerEdge() {
    Graph.registerEdge(
      'edge',
      {
        inherit: 'edge',
        attrs: {
          line: {
            stroke: '#A2B1C3',
            strokeWidth: 2,
          }
        },
        connector: {
          name: 'rounded'
        },
        router: {
          name: 'orth',
          args: {
            startDirections: ['left', 'right', 'top'],
            endDirections: ['right', 'left', 'bottom'],
            padding: 20, // Default is 10; increase for more spacing
            offset: 20   // Adds space before turning
          }
        }
      },
      true
    );

    Graph.registerEdge(
      'success-edge',
      {
        inherit: 'edge',
        attrs: {
          line: {
            stroke: '#A2B1C3',
            strokeWidth: 2,
          }
        },
        connector: {
          name: 'rounded'
        },
        router: {
          name: 'orth',
          args: {
            startDirections: ['left', 'right', 'top'],
            endDirections: ['right', 'left', 'top'],
            padding: 20, // Default is 10; increase for more spacing
            offset: 20   // Adds space before turning
          }
        }
      },
      true
    );

    Graph.registerEdge(
      'failure-edge',
      {
        inherit: 'edge',
        attrs: {
          line: {
            stroke: '#A2B1C3',
            strokeWidth: 2,
          }
        },
        connector: {
          name: 'rounded'
        },
        router: {
          name: 'orth',
          args: {
            startDirections: ['left', 'right', 'top'],
            endDirections: ['right', 'left', 'top'],
            padding: 20, // Default is 10; increase for more spacing
            offset: 20   // Adds space before turning
          }
        }
      },
      true
    );
  }

  getPortsConfig() {
    return {
      groups: {
        top: this.portGroup('top'),
        right: this.portGroup('right', true),
        bottom: this.portGroup('bottom'),
        left: this.portGroup('left'),
      },
      items: [
        { group: 'top' },
        { group: 'right' },
        { group: 'bottom' },
        { group: 'left' },
      ],
    };
  }

  portGroup(position: string, isOutput = false) {
    return {
      position,
      attrs: {
        body: {
          width: 16,
          height: 16,
          magnet: true,
          fill: '#fff',
          stroke: '#5F95FF',
          strokeWidth: 1,
          style: {
            // visibility: 'hidden',
          },
        },
      },
      markup: [{ tagName: 'rect', selector: 'body' }],
      label: { position: { name: 'center' } },
    };
  }


  registerCustomTools() {
    this.registerCommentButton();
    this.registerHoverAddButton();
  }

  private registerCommentButton() {
    Graph.registerNodeTool('commentButton', {
      name: 'commentButton',
      inherit: 'button'
    }, true);
  }

  private registerHoverAddButton() {
    Graph.registerNodeTool('hover-add-button', {
      inherit: 'button'
    }, true);
  }
}
